/**
 * PATH: packages/email-hub/src/feature/components/rich-text/linkPrompt.ts
 * FILE_ID: EMAILHUB-RICHTEXT-LINKPROMPT-001
 * PURPOSE: Single source of truth for the "insert/edit link" prompt so the
 *          Toolbar button and the right-click context menu behave identically
 *          (same scheme coercion, same unset-on-empty).
 * FLOW: editor → promptDialog(URL) → setLink / unsetLink
 */

import { promptDialog } from '@quotech/ui'
import type { Editor } from '@tiptap/core'

export async function promptInsertLink(editor: Editor | null): Promise<void> {
  if (!editor) return
  const prev = editor.getAttributes('link').href as string | undefined
  const url = await promptDialog({
    title: 'Insert link', inputLabel: 'URL', placeholder: 'https://example.com',
    defaultValue: prev || '', submitLabel: 'Insert link',
  })
  if (url === null) return
  if (url === '') { editor.chain().focus().unsetLink().run(); return }
  const safe = /^(https?:|mailto:|tel:)/i.test(url) ? url : `https://${url}`

  // No text selected and not editing an existing link → there's nothing to
  // carry the link mark, so `setLink` alone would insert an invisible link.
  // Insert the URL itself as the visible, clickable text instead (operator
  // 2026-06-29). When text IS selected (or the caret sits in an existing
  // link), hyperlink/-edit that range as before.
  const { empty } = editor.state.selection
  if (empty && !editor.isActive('link')) {
    editor.chain().focus()
      .insertContent({ type: 'text', text: url, marks: [{ type: 'link', attrs: { href: safe } }] })
      .run()
    return
  }
  editor.chain().focus().extendMarkRange('link').setLink({ href: safe }).run()
}
